import React, { FunctionComponent, useMemo, useState } from 'react';
import { KeyboardDoubleArrowUp, KeyboardDoubleArrowDown, KeyboardArrowLeft, KeyboardArrowRight } from '../../components/icons';
import Typography from '../Typography/Typography';
import IconButton from '../IconButton/IconButton';
import CircularProgress from '../CircularProgress/CircularProgress';
import { TableColumns } from '../../types/table';
import { sortRowsByKey, SortDirection } from '../../utils/tableSort';

type TableUIProps = {
  // Only used by the legacy OnOrderByTitles path. When `sortTableColumns` is supplied it is
  // the single source of truth for the header row, and this is ignored - don't pass both.
  headers?: string[],
  generateBody: Function,
  rows: any,
  defaultRowsPerPage?: number,
  OnOrderByTitles?: Function,
  pagination?: boolean,
  skipIndexCol?: boolean,
  bottomRow?: string[],
  loading?: boolean,
  emptyTableText?: string,
  displayEmptyText?: boolean,
  displaySortAscending?: boolean,
  sortTableColumns?: TableColumns[],
  isFlightSchedule?: boolean,
  // Optional tiebreaker column key. When two rows tie on the clicked column they are
  // ordered by this key, always ascending - see compareRowValues in utils/tableSort.
  secondarySortKey?: string,
};

// Appearance now comes from `.ds-table thead th` (see design-system.css), so
// this only carries the interaction affordance.
const thClassBase = 'select-none';
const thClass = `${thClassBase} cursor-pointer`;
// Columns marked `sortable: false` keep the same look but drop the pointer cursor, so a
// header that can't sort doesn't advertise itself as clickable.
const thClassStatic = thClassBase;

// Tailwind rewrite of the shared sortable/paginated table. All sort/pagination
// logic is preserved verbatim from the MUI wrapper; only the presentation
// (plain <table> + custom pagination bar) changed.
const TableUI: FunctionComponent<TableUIProps> = ({ sortTableColumns, headers, displaySortAscending, loading = false, rows, generateBody, defaultRowsPerPage, OnOrderByTitles, pagination, skipIndexCol, bottomRow, displayEmptyText = false, emptyTableText = 'No data in table', isFlightSchedule = false, secondarySortKey }) => {
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: SortDirection } | null>(null);

  // Derived rather than state: the visible order is always a pure function of the incoming
  // `rows` and the chosen sort. Previously `data` was state that a `useEffect` reset from
  // `rows` on every parent render, which silently threw away the user's sort (a refetch, or
  // even a keystroke in a filter box) *and* left `sortConfig` stale - so the next click on
  // that column computed 'desc' and opened descending out of nowhere.
  const data = useMemo(
    () => (sortConfig === null
      ? rows
      : sortRowsByKey<any>(rows, sortConfig.key, sortConfig.direction, isFlightSchedule, secondarySortKey)),
    [rows, sortConfig, isFlightSchedule, secondarySortKey],
  );

  const dynamicSort = (key: string) => {
    // Only re-clicking the same column toggles; a click on a different column always
    // starts ascending instead of inheriting the previous column's direction.
    setSortConfig((previous) => ({
      key,
      direction: previous?.key === key && previous.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const [page, setPage] = useState(0);
  const [sortByColumn, setSortByColumn] = useState('');
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const onOrderClick = (header: string) => {
    if (OnOrderByTitles !== undefined) {
      OnOrderByTitles(header);
      setSortByColumn(header);
    }
  };

  // Arrow for the active column. On the sortTableColumns path the direction comes from
  // `sortConfig`, which this component owns - no caller wiring needed. On the legacy
  // headers/OnOrderByTitles path the parent does the sorting, so it has to tell us the
  // direction via `displaySortAscending`; without it we can't honestly draw an arrow.
  const sortIcon = (label: string, columnKey?: string) => {
    if (label === '') return '';

    const isDynamic = columnKey !== undefined;
    if (isDynamic ? sortConfig?.key !== columnKey : sortByColumn !== label) return '';

    const ascending = isDynamic ? sortConfig?.direction === 'asc' : displaySortAscending;
    if (ascending === undefined) return '';

    return ascending
      ? <IconButton color="primary"><KeyboardDoubleArrowUp /> </IconButton>
      : <IconButton color="primary"><KeyboardDoubleArrowDown /> </IconButton>;
  };

  if (loading !== false) {
    return (
      <div className="flex justify-center"><div><CircularProgress /></div></div>
    );
  }

  // Counted off `data`, the list actually rendered below, rather than the `rows` prop.
  const lastPage = rowsPerPage ? Math.max(0, Math.ceil(data.length / rowsPerPage) - 1) : 0;
  const from = data.length === 0 ? 0 : page * (rowsPerPage ?? 0) + 1;
  const to = Math.min(data.length, (page + 1) * (rowsPerPage ?? 0));

  return (
    <div>
      <div className="w-full overflow-auto scrollbar-thin">
        <table className="ds-table w-full">
          <thead>
            <tr>
              {skipIndexCol !== true && <th className={thClass}>{''}</th>}
              {sortTableColumns === undefined
                ? (headers ?? []).map((header, index) => (
                    <th
                      key={index}
                      className={OnOrderByTitles === undefined ? thClassStatic : thClass}
                      onClick={OnOrderByTitles === undefined ? undefined : () => onOrderClick(header)}
                    >
                      {header}{sortIcon(header)}
                    </th>
                  ))
                : sortTableColumns.map((column, idx) => (
                    <th
                      key={idx}
                      className={column.sortable === false ? thClassStatic : thClass}
                      onClick={column.sortable === false ? undefined : () => dynamicSort(column.key)}
                    >
                      {column.label}{sortIcon(column.label, column.key)}
                    </th>
                  ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && displayEmptyText === true
              ? (
                <tr>
                  {/* Includes the leading index column, which the header renders whenever
                      skipIndexCol isn't true - otherwise this row comes up one column short. */}
                  <td
                    colSpan={(sortTableColumns ?? headers ?? []).length + (skipIndexCol !== true ? 1 : 0)}
                    className="p-4"
                  >
                    <Typography text={emptyTableText} align="center" variant="subtitle1" fontWeight="bold" />
                  </td>
                </tr>
              )
              : pagination === true
                ? data.slice(page * rowsPerPage!, page * rowsPerPage! + rowsPerPage!).map((row: any, index: number) => generateBody(row, page * rowsPerPage! + index))
                : data.map((row: any, index: number) => generateBody(row, index + 1))}
            {bottomRow !== undefined && (
              <tr>
                {bottomRow.map((column, idx) => (
                  <td key={idx} className="font-semibold">
                    <Typography variant="subtitle1" fontWeight="bold" text={column} />
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination === true && (
        <div className="flex items-center justify-end gap-4 px-2 py-2 text-sm text-content">
          <span>Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            className="cursor-pointer border-0 border-b border-line bg-transparent py-0.5 outline-none"
          >
            {[10, 25, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>{from}–{to} of {data.length}</span>
          <IconButton disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}><KeyboardArrowLeft /></IconButton>
          <IconButton disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))}><KeyboardArrowRight /></IconButton>
        </div>
      )}
    </div>
  );
};
export default TableUI;
